'use client';

import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface CheckboxProps
  extends Omit<React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>, 'children'> {
  label?: React.ReactNode;
}

export const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(function Checkbox({ className, label, id, disabled, ...rest }, ref) {
  const control = (
    <CheckboxPrimitive.Root
      ref={ref}
      id={id}
      disabled={disabled}
      className={cn(
        'flex size-[18px] shrink-0 items-center justify-center rounded-xs border-[1.5px]',
        'border-border-strong bg-surface transition-[background-color,border-color] duration-[var(--duration-fast)]',
        'data-[state=checked]:border-accent data-[state=checked]:bg-accent',
        'data-[state=indeterminate]:border-accent data-[state=indeterminate]:bg-accent',
        'cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...rest}
    >
      <CheckboxPrimitive.Indicator className="flex items-center justify-center text-accent-fg">
        {rest.checked === 'indeterminate' ? (
          <Minus className="size-3" strokeWidth={3.5} />
        ) : (
          <Check className="size-3" strokeWidth={3.5} />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );

  if (!label) return control;

  return (
    <label
      htmlFor={id}
      className={cn(
        'inline-flex items-center gap-[var(--space-2-5)] text-base text-fg',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      )}
    >
      {control}
      <span>{label}</span>
    </label>
  );
});
